<template>
  <ElDialog
    v-model="visible"
    title="系统更新内容"
    :width="dialogWidth"
    append-to-body
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
  >
    <div class="upgrade-notice">
      <div class="upgrade-title">{{ notice?.logTitle || "本次更新内容" }}</div>
      <div class="upgrade-content">{{ notice?.logContent || "暂无更新说明" }}</div>
    </div>

    <template #footer>
      <ElButton @click="handleConfirm">确定</ElButton>
      <ElButton v-if="!isMobile" type="primary" @click="handleGoTimeline">查看更多</ElButton>
    </template>
  </ElDialog>
</template>

<script setup lang="ts">
import { SysUpdateLogApi, UpgradeNoticeVO } from "@/api/main/sys/updatelog";

defineOptions({ name: "UpgradeNoticeDialog" });

const visible = ref(false);
const notice = ref<UpgradeNoticeVO | null>(null);
const router = useRouter();
const route = useRoute();
const isMobile = ref(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
const dialogWidth = computed(() => (isMobile.value ? "350px" : "720px"));

const STORAGE_KEY = "PENDING_UPGRADE_NOTICE";

const readNoticeFromStorage = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const data = JSON.parse(raw) as UpgradeNoticeVO;
    if (!data?.show) return;
    notice.value = data;
    visible.value = true;
  } catch (error) {
    console.error("读取升级提示失败:", error);
  }
};

const clearNotice = () => {
  sessionStorage.removeItem(STORAGE_KEY);
  visible.value = false;
};

const markRead = async () => {
  if (!notice.value) return;
  try {
    await SysUpdateLogApi.markUpgradeNoticeRead({
      versionCode: notice.value.versionCode,
      logId: notice.value.logId,
    });
  } catch (error) {
    console.error("标记升级提示已读失败:", error);
  }
};

const handleConfirm = async () => {
  await markRead();
  clearNotice();
};

const handleGoTimeline = async () => {
  await markRead();
  clearNotice();
  const targetByName = router.getRoutes().find((r) => r.name === "SysUpdateLogView");
  if (targetByName) {
    await router.push({ name: "SysUpdateLogView" });
    return;
  }

  const targetByPath = router
    .getRoutes()
    .find((r) => r.path.includes("sys/updatelog") || r.path.includes("sys-update-log"));
  if (targetByPath) {
    await router.push({ path: targetByPath.path });
    return;
  }

  await router.push({ path: "/" });
};

onMounted(() => {
  readNoticeFromStorage();
});

watch(
  () => route.fullPath,
  () => {
    readNoticeFromStorage();
  },
);
</script>

<style lang="scss" scoped>
.upgrade-notice {
  .upgrade-title {
    margin-bottom: 8px;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
  }

  .upgrade-content {
    max-height: 280px;
    overflow-y: auto;
    color: #606266;
    font-size: 14px;
    line-height: 1.7;
    white-space: pre-line;
  }
}
</style>
